import React, { useState } from 'react';
import {
    TextField,
    Button,
    Grid,
    Typography,
    IconButton,
    Box,
    Collapse,
    Tooltip,
    Fade,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    School as SchoolIcon,
    Work as WorkIcon,
    Person as PersonIcon,
    Psychology as PsychologyIcon,
    ExpandMore as ExpandMoreIcon,
    CloudUpload as CloudUploadIcon,
    EmojiEvents as CertIcon,
    FolderSpecial as ProjectIcon,
    Language as LanguageIcon,
    Link as LinkIcon,
} from '@mui/icons-material';

function Form({ formData, setFormData }) {
    const [expandedSections, setExpandedSections] = useState({
        personal: true,
        education: true,
        experience: true,
        skills: true,
        certifications: false,
        projects: false,
        languages: false,
        socialLinks: false,
    });

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData({ ...formData, photo: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const handleEducationChange = (index, e) => {
        const updatedEducation = formData.education.map((edu, i) =>
            i === index ? { ...edu, [e.target.name]: e.target.value } : edu
        );
        setFormData({ ...formData, education: updatedEducation });
    };

    const handleExperienceChange = (index, e) => {
        const updatedExperience = formData.experience.map((exp, i) =>
            i === index ? { ...exp, [e.target.name]: e.target.value } : exp
        );
        setFormData({ ...formData, experience: updatedExperience });
    };

    const addEducation = () => {
        setFormData({
            ...formData,
            education: [...formData.education, { institution: '', degree: '', startYear: '', endYear: '' }],
        });
    };

    const deleteEducation = (index) => {
        if (formData.education.length <= 1) return;
        setFormData({
            ...formData,
            education: formData.education.filter((_, i) => i !== index),
        });
    };

    const addExperience = () => {
        setFormData({
            ...formData,
            experience: [...formData.experience, { company: '', role: '', startYear: '', endYear: '' }],
        });
    };

    const deleteExperience = (index) => {
        if (formData.experience.length <= 1) return;
        setFormData({
            ...formData,
            experience: formData.experience.filter((_, i) => i !== index),
        });
    };

    const removePhoto = () => {
        setFormData({ ...formData, photo: null });
    };

    const handleCertificationChange = (index, e) => {
        const updated = formData.certifications.map((cert, i) =>
            i === index ? { ...cert, [e.target.name]: e.target.value } : cert
        );
        setFormData({ ...formData, certifications: updated });
    };

    const addCertification = () => {
        setFormData({
            ...formData,
            certifications: [...formData.certifications, { name: '', issuer: '', year: '' }],
        });
    };

    const deleteCertification = (index) => {
        if (formData.certifications.length <= 1) return;
        setFormData({
            ...formData,
            certifications: formData.certifications.filter((_, i) => i !== index),
        });
    };

    const handleProjectChange = (index, e) => {
        const updated = formData.projects.map((proj, i) =>
            i === index ? { ...proj, [e.target.name]: e.target.value } : proj
        );
        setFormData({ ...formData, projects: updated });
    };

    const addProject = () => {
        setFormData({
            ...formData,
            projects: [...formData.projects, { title: '', description: '', technologies: '', link: '' }],
        });
    };

    const deleteProject = (index) => {
        if (formData.projects.length <= 1) return;
        setFormData({
            ...formData,
            projects: formData.projects.filter((_, i) => i !== index),
        });
    };

    const handleSocialLinkChange = (e) => {
        setFormData({
            ...formData,
            socialLinks: { ...formData.socialLinks, [e.target.name]: e.target.value },
        });
    };

    const SectionHeader = ({ icon: Icon, title, section }) => (
        <Box
            className="section-header"
            onClick={() => toggleSection(section)}
        >
            <Icon className="section-icon" />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
                {title}
            </Typography>
            <ExpandMoreIcon
                sx={{
                    transform: expandedSections[section] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    color: 'text.secondary',
                }}
            />
        </Box>
    );

    return (
        <Box>
            {/* Personal Information */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={PersonIcon} title="Personal Information" section="personal" />
                <Collapse in={expandedSections.personal}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    {formData.photo ? (
                                        <Fade in>
                                            <Box position="relative" display="inline-block">
                                                <img src={formData.photo} alt="Profile" className="photo-preview" />
                                                <Tooltip title="Remove photo">
                                                    <IconButton
                                                        size="small"
                                                        className="delete-btn"
                                                        onClick={removePhoto}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -4,
                                                            right: -4,
                                                            bgcolor: 'background.paper',
                                                            boxShadow: 1,
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Fade>
                                    ) : null}
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        className="add-btn"
                                        size="small"
                                    >
                                        {formData.photo ? 'Change Photo' : 'Upload Photo'}
                                        <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="name" label="Full Name" fullWidth value={formData.name} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="email" label="Email" fullWidth value={formData.email} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="phone" label="Phone" fullWidth value={formData.phone} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="address" label="Address" fullWidth value={formData.address} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="summary"
                                    label="Professional Summary"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={formData.summary}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Box>

            {/* Education */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={SchoolIcon} title="Education" section="education" />
                <Collapse in={expandedSections.education}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        {formData.education.map((edu, index) => (
                            <Fade in key={index}>
                                <Box className="entry-card" sx={{ p: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Education #{index + 1}
                                        </Typography>
                                        {formData.education.length > 1 && (
                                            <Tooltip title="Remove">
                                                <IconButton size="small" className="delete-btn" onClick={() => deleteEducation(index)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="institution"
                                                label="Institution"
                                                fullWidth
                                                value={edu.institution}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="degree"
                                                label="Degree"
                                                fullWidth
                                                value={edu.degree}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                name="startYear"
                                                label="Start Year"
                                                fullWidth
                                                value={edu.startYear}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                name="endYear"
                                                label="End Year"
                                                fullWidth
                                                value={edu.endYear}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>
                        ))}
                        <Button
                            className="add-btn"
                            startIcon={<AddIcon />}
                            onClick={addEducation}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            Add Education
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Experience */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={WorkIcon} title="Experience" section="experience" />
                <Collapse in={expandedSections.experience}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        {formData.experience.map((exp, index) => (
                            <Fade in key={index}>
                                <Box className="entry-card" sx={{ p: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Experience #{index + 1}
                                        </Typography>
                                        {formData.experience.length > 1 && (
                                            <Tooltip title="Remove">
                                                <IconButton size="small" className="delete-btn" onClick={() => deleteExperience(index)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="company"
                                                label="Company"
                                                fullWidth
                                                value={exp.company}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="role"
                                                label="Role"
                                                fullWidth
                                                value={exp.role}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                name="startYear"
                                                label="Start Year"
                                                fullWidth
                                                value={exp.startYear}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                name="endYear"
                                                label="End Year"
                                                fullWidth
                                                value={exp.endYear}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>
                        ))}
                        <Button
                            className="add-btn"
                            startIcon={<AddIcon />}
                            onClick={addExperience}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            Add Experience
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Skills */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={PsychologyIcon} title="Skills" section="skills" />
                <Collapse in={expandedSections.skills}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <TextField
                            name="skills"
                            label="Skills (comma separated)"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.skills}
                            onChange={handleChange}
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* Certifications */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={CertIcon} title="Certifications" section="certifications" />
                <Collapse in={expandedSections.certifications}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        {formData.certifications.map((cert, index) => (
                            <Fade in key={index}>
                                <Box className="entry-card" sx={{ p: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Certification #{index + 1}
                                        </Typography>
                                        {formData.certifications.length > 1 && (
                                            <Tooltip title="Remove">
                                                <IconButton size="small" className="delete-btn" onClick={() => deleteCertification(index)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={5}>
                                            <TextField
                                                name="name"
                                                label="Certification Name"
                                                fullWidth
                                                value={cert.name}
                                                onChange={(e) => handleCertificationChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                name="issuer"
                                                label="Issuing Organization"
                                                fullWidth
                                                value={cert.issuer}
                                                onChange={(e) => handleCertificationChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                name="year"
                                                label="Year"
                                                fullWidth
                                                value={cert.year}
                                                onChange={(e) => handleCertificationChange(index, e)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>
                        ))}
                        <Button
                            className="add-btn"
                            startIcon={<AddIcon />}
                            onClick={addCertification}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            Add Certification
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Projects */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={ProjectIcon} title="Projects" section="projects" />
                <Collapse in={expandedSections.projects}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        {formData.projects.map((proj, index) => (
                            <Fade in key={index}>
                                <Box className="entry-card" sx={{ p: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Project #{index + 1}
                                        </Typography>
                                        {formData.projects.length > 1 && (
                                            <Tooltip title="Remove">
                                                <IconButton size="small" className="delete-btn" onClick={() => deleteProject(index)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="title"
                                                label="Project Title"
                                                fullWidth
                                                value={proj.title}
                                                onChange={(e) => handleProjectChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                name="technologies"
                                                label="Technologies Used"
                                                fullWidth
                                                value={proj.technologies}
                                                onChange={(e) => handleProjectChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                name="description"
                                                label="Description"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={proj.description}
                                                onChange={(e) => handleProjectChange(index, e)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                name="link"
                                                label="Project Link"
                                                fullWidth
                                                value={proj.link}
                                                onChange={(e) => handleProjectChange(index, e)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>
                        ))}
                        <Button
                            className="add-btn"
                            startIcon={<AddIcon />}
                            onClick={addProject}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            Add Project
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Languages */}
            <Box className="form-section" sx={{ mb: 2 }}>
                <SectionHeader icon={LanguageIcon} title="Languages" section="languages" />
                <Collapse in={expandedSections.languages}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <TextField
                            name="languages"
                            label="Languages (comma separated)"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.languages}
                            onChange={handleChange}
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* Social Links */}
            <Box className="form-section">
                <SectionHeader icon={LinkIcon} title="Social Links" section="socialLinks" />
                <Collapse in={expandedSections.socialLinks}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <TextField
                                    name="linkedin"
                                    label="LinkedIn URL"
                                    fullWidth
                                    value={formData.socialLinks.linkedin}
                                    onChange={handleSocialLinkChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="github"
                                    label="GitHub URL"
                                    fullWidth
                                    value={formData.socialLinks.github}
                                    onChange={handleSocialLinkChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="website"
                                    label="Portfolio / Website"
                                    fullWidth
                                    value={formData.socialLinks.website}
                                    onChange={handleSocialLinkChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
}

export default Form;
